export const ROUTES = {
  welcome: 'welcome',
  login: 'auth/login',
  register: 'auth/register',
  resetPassword: 'auth/reset-password',

  home: 'home',
  profile: 'profile',
  changePassword: 'profile/change-password',
  services: 'services',
  notifications: 'notifications',
  supportContact: 'support/contact',
  supportRequests: 'support/requests',
  supportRequestCreate: 'support/requests/create',
  supportChat: 'support/requests/:requestId/chat',

  faq: 'faq',
  deleteAccountInstructions: 'faq/delete-account-instructions',
  privacyPolicy: 'privacy-policy',

  preApproval: 'pre-approval',
  // :step необязателен: вендор биометрии возвращает на адрес без шага (?biometryStatus=)
  preApprovalApply: 'pre-approval/apply/:step?',
  preApprovalCalculator: 'pre-approval/calculator',
  preApprovalCars: 'pre-approval/cars',
  preApprovalCarDetail: 'pre-approval/cars/:modelId'
};

/** Шаги заявки на предодобрение — значения `:step` в `preApprovalApply`. */
export const PRE_APPROVAL_APPLY_STEPS = {
  consent: 'consent',
  phone: 'phone',
  biometry: 'biometry',
  result: 'result'
} as const;

export type TPreApprovalApplyStep =
  (typeof PRE_APPROVAL_APPLY_STEPS)[keyof typeof PRE_APPROVAL_APPLY_STEPS];

/** Путь к шагу заявки (подставляет step в шаблон `preApprovalApply`). */
export const buildPreApprovalApplyPath = (
  step: TPreApprovalApplyStep,
  search?: string
): string => {
  const path = ROUTES.preApprovalApply.replace(':step?', step);
  return search ? `${path}?${search}` : path;
};

/** Имена query-параметров подбора авто. `city` совпадает с тем, что уходит в автокредит. */
export const PRE_APPROVAL_PARAM = {
  city: 'city',
  term: 'term',
  down: 'down'
} as const;

/**
 * Условия подбора авто, зашитые в ссылку. Лежат в URL, а не в sessionStorage, чтобы
 * страницы Cars/CarDetail работали в новой вкладке и по расшаренной ссылке.
 */
export interface IPreApprovalSearchParams {
  cityId: string | null;
  term: number;
  downPaymentAmount: number;
}

/** Query-строка условий подбора. Единственное место, где задан их формат. */
export const buildPreApprovalSearch = ({
  cityId,
  term,
  downPaymentAmount
}: IPreApprovalSearchParams): string => {
  const search = new URLSearchParams();
  if (cityId) search.set(PRE_APPROVAL_PARAM.city, cityId);
  search.set(PRE_APPROVAL_PARAM.term, String(term));
  search.set(PRE_APPROVAL_PARAM.down, String(Math.round(downPaymentAmount)));
  return search.toString();
};

/** Путь к списку авто с условиями подбора. */
export const buildPreApprovalCarsPath = (
  params: IPreApprovalSearchParams
): string => `${ROUTES.preApprovalCars}?${buildPreApprovalSearch(params)}`;

/** Путь к калькулятору с предзаполнением формы из ссылки. */
export const buildPreApprovalCalculatorPath = (
  params: IPreApprovalSearchParams
): string =>
  `${ROUTES.preApprovalCalculator}?${buildPreApprovalSearch(params)}`;

/** Путь к карточке авто (подставляет modelId в шаблон `preApprovalCarDetail`). */
export const buildPreApprovalCarDetailPath = (
  modelId: string,
  params: IPreApprovalSearchParams
): string =>
  `${ROUTES.preApprovalCarDetail.replace(':modelId', encodeURIComponent(modelId))}?${buildPreApprovalSearch(params)}`;

/** Путь к чату по обращению (подставляет requestId в шаблон `supportChat`). */
export const buildSupportChatPath = (requestId: string): string =>
  ROUTES.supportChat.replace(':requestId', encodeURIComponent(requestId));

export const WEBVIEW_ROUTES = {
  autoCredit: 'auto-credit',
  leasing: 'leasing',
  vehicleManagement: 'vehicle-management'
};
